import PhoneInput from "../../../../components/PhoneInput";
import { toPhoneDigits } from "../../../../helper/phoneUtils";
import { Datepicker } from "@mobiscroll/react";
import { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import AsyncTypeahead from "../../../../components/AsyncTypeahead";
import { createPatientApi } from "../../../../api-helpers/PatientApiHelper";
import { AddPersonalContact, PhoneTypesResponse, UpdatePersonalContact } from "patients-experience-client";
import Spinner from "../../../../components/Spinner";
import Toaster, { ToastConfig } from "../../../../components/Toaster";
import { ErrorTypes } from "../../../../interfaces/errors";
import { createContactTypeApi } from "../../../../api-helpers/CommonUtilsApiHelper";
import { IdValuePairDTO } from "common-util-experience-client";
import { useParams } from "react-router-dom";

export const AddUpdatePersonal = (props: any) => {
    const patientApi = createPatientApi();
    const contactTypesApi = createContactTypeApi();
    const [contactTypes, setContactTypes] = useState<Array<IdValuePairDTO>>([]);
    const [phoneTypes, setPhoneTypes] = useState<Array<PhoneTypesResponse>>([]);
    const [loading, setloading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [validated, setValidated] = useState(false);
    const { patientId } = useParams();
    
    const [form, setForm] = useState<UpdatePersonalContact | AddPersonalContact>({
        contactPreference: props.selectedContact?.contactPreference || "",
        contactTypeId: props.selectedContact?.contactType.id || "",
        credential: props.selectedContact?.contactCredential || "",
        firstName: props.selectedContact?.contactFirstName || "",
        lastName: props.selectedContact?.contactLastName || "",
        phone: toPhoneDigits(props.selectedContact?.phone),
        email: props.selectedContact?.email || "",
        phoneTypeId: props.selectedContact?.phoneType.id || ""
    });
    const [toastConfig, setToastConfig] = useState<ToastConfig>({
        type: "success" as ErrorTypes,
        title: "",
        icon: "",
    });

    useEffect(() => {
        loadContactTypes();
        loadPhoneTypes();
    }, [])

    const loadContactTypes = async () => {
        setloading(true);
        try {
            const response = await contactTypesApi.contactTypeControllerGetAll('true');
            setContactTypes(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setloading(false);
        }
    }

    const loadPhoneTypes = async () => {
        setloading(true);
        try {
            const response = await patientApi.patientsControllerGetphoneTypes();
            setPhoneTypes(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setloading(false);
        }
    }

    const handleDiscard = () => {
        props.setOffCanvasShow(false);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        const phoneInput = document.querySelector('[name="phone"]') as HTMLInputElement;
        phoneInput.setCustomValidity("");
        event.preventDefault();
        setValidated(true);

        const addForm = event.currentTarget;
        if (!addForm.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (form?.phone?.length !== 10) {
            phoneInput.setCustomValidity("Invalid Phone Number");
            return;
        } else {
            phoneInput.setCustomValidity("");
        }
        setloading(true);
        try {
            if (props.selectedContact == undefined) {
                await patientApi.patientsControllerCreatePatientPersonalContact(form, patientId as string);
            } else {
                await patientApi.patientsControllerUpdatePersonalContact(form, props.selectedContact.patientId, props.selectedContact.contactId);
            }
            setloading(false);
            showSuccessMessage();
            setTimeout(() => {
                handleDiscard();
            }, 1500);
            props.getPatientContactInfo();
        } catch (error: any) {
            console.error(error);
            setloading(false);
            showErrorMessage(error.response.data.message.message);
        }
    };

    const showSuccessMessage = () => {
        setShowToast(false);
        const toasterConfig: ToastConfig = {
            type: "success",
            title: "Patient personal contact updated successfully!",
            icon: "check_circle_outline",
        };
        setToastConfig(toasterConfig);
        setShowToast(true);
    }

    const showErrorMessage = (message: string) => {
        setShowToast(false);
        const toasterConfig: ToastConfig = {
            type: "danger",
            title: message || "Mobile number must be exactly 10 digits.",
            icon: "error_outline",
        };
        setToastConfig(toasterConfig);
        setShowToast(true);
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'phone' && value.length < 10) {
            setForm({ ...form, [name]: value });
            setValidated(false);
            return;
        }
        setForm({
            ...form,
            [name]: value
        });
    };

    return (
        <>
            {loading && <Spinner />}
            <div className="row">
                <Toaster
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    type={toastConfig.type}
                    title={toastConfig.title}
                    message={toastConfig.message}
                    icon={toastConfig.icon}
                />
            </div>
            <Form
                noValidate
                validated={validated}
                onSubmit={submitHandler}
                className="d-flex flex-column h-100">
                <div className="flex-fill">
                    <div className="row g-2 mb-3">
                        <div className="col-6">
                            <div className="mb-3">
                                <label
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    Preference
                                </label>
                                <select value={form.contactPreference} required name="contactPreference" onChange={handleInputChange}
                                    className="form-select"
                                >
                                    <option value="">Select</option>
                                    <option value="P" selected>Primary</option>
                                    <option value="S">Secondary</option>
                                    <option value="O">Other</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="mb-3">
                                <label
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    Relation
                                </label>
                                <select value={form.contactTypeId} required
                                    name="contactTypeId" onChange={handleInputChange}
                                    className="form-select"
                                >
                                    <option value="">Select</option>
                                    {
                                        contactTypes.map((type: IdValuePairDTO) =>
                                            <option value={type.id} selected>{type.value}</option>
                                        )
                                    }
                                </select>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="mb-3">
                                <label
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    First Name
                                </label>
                                <div className="input-group mb-3">
                                    <div className="col-4">
                                        <input type="text" className="form-control"
                                            placeholder="Cred." 
                                            name="credential" onChange={handleInputChange}
                                            value={form.credential} />
                                    </div>
                                    <div className="col-8">
                                        <input type="text" className="form-control"
                                            placeholder="First Name" name="firstName" onChange={handleInputChange}
                                            value={form.firstName} required />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="mb-3">
                                <label
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    Last Name
                                </label>
                                <input type="text" className="form-control" placeholder="Last Name"
                                    name="lastName" onChange={handleInputChange}
                                    value={form.lastName} />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="mb-3">
                                <label
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    Phone Type
                                </label>
                                <select value={form.phoneTypeId} required
                                    name="phoneTypeId" onChange={handleInputChange}
                                    className="form-select"
                                >
                                    <option value={""}>Select</option>
                                    {
                                        phoneTypes.map((type: PhoneTypesResponse) =>
                                            <option value={type.id} selected>{type.name}</option>
                                        )
                                    }
                                </select>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="mb-3">
                                <label
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    Mobile
                                </label>
                                <PhoneInput name="phone" placeholder="Mobile"
                                    onChange={handleInputChange}
                                    value={form.phone} required />
                            </div>
                        </div>
                        
                        <div className="col-12">
                            <div className="mb-3">
                                <label
                                    className="form-label fs-12 fw-semibold mb-1"
                                >
                                    Email
                                </label>
                                <input type="email" className="form-control" placeholder="Email"
                                    name="email" onChange={handleInputChange}
                                    value={form.email} required />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="offcanvas-footer text-end w-100 border-top d-flex justify-content-between border-top">
                    <div>
                        <button
                            className="btn btn-outline-secondary rounded-pill px-5 fs-13"
                            type="reset"
                            onClick={handleDiscard}
                        >
                            Discard
                        </button>
                    </div>
                    <div>
                        <button
                            className="btn btn-secondary rounded-pill ps-4 pe-5 fs-13"
                            type="submit"
                        >
                            {props.selectedContact ? 'Update' : (
                                <>
                                    Add</>)}
                        </button>
                    </div>
                </div>
            </Form>
        </>
    );
};
